import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { LogService } from './log.service';
import { SupabaseService } from './supabase.service';

export interface StartCheckoutOptions {
  preopen?: boolean;
  couponCode?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private supabaseService = inject(SupabaseService);
  private supabase = this.supabaseService.client;
  private authService = inject(AuthService);
  private logService = inject(LogService);
  private readonly checkoutFunction = 'mercadopago-checkout';

  private openCheckoutLoadingTab(): Window | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const popup = window.open('', '_blank');
    if (!popup) {
      return null;
    }

    try {
      popup.document.open();
      popup.document.write(
        '<!doctype html><html><head><meta charset="utf-8"><title>Abrindo checkout...</title>' +
        '<style>body{margin:0;background:#020617;color:#e2e8f0;font-family:Segoe UI,Arial,sans-serif;display:grid;place-items:center;height:100vh}' +
        '.card{background:#0f172a;border:1px solid #334155;padding:24px 20px;border-radius:16px;min-width:280px;text-align:center}' +
        '.dot{width:10px;height:10px;border-radius:9999px;background:#3b82f6;display:inline-block;animation:p 1s infinite ease-in-out}' +
        '.dot:nth-child(2){animation-delay:.15s}.dot:nth-child(3){animation-delay:.3s}@keyframes p{0%,100%{transform:scale(.7);opacity:.6}50%{transform:scale(1);opacity:1}}</style></head>' +
        '<body><div class="card"><h1 style="font-size:16px;margin:0 0 10px">Abrindo checkout</h1>' +
        '<p style="font-size:13px;opacity:.8;margin:0 0 14px">Estamos preparando seu pagamento...</p>' +
        '<div><span class="dot"></span> <span class="dot"></span> <span class="dot"></span></div></div></body></html>'
      );
      popup.document.close();
    } catch {
      // no-op
    }

    return popup;
  }

  private async resolveAccessToken(): Promise<string | null> {
    const inMemoryToken = this.authService.session()?.access_token?.trim();
    if (inMemoryToken) {
      return inMemoryToken;
    }

    const { data: sessionData, error: sessionError } = await this.supabase.auth.getSession();
    if (sessionError) {
      throw new Error(`Falha ao validar sessão: ${sessionError.message}`);
    }

    return sessionData?.session?.access_token?.trim() ?? null;
  }

  private async getFunctionErrorMessage(error: any): Promise<string> {
    const fallback = error?.message ?? 'Erro ao iniciar pagamento.';
    const response = error?.context;

    if (!response || typeof response !== 'object') {
      return fallback;
    }

    try {
      const cloned = typeof response.clone === 'function' ? response.clone() : response;
      const payload = await cloned.json();
      return payload?.error ?? payload?.message ?? fallback;
    } catch {
      try {
        const cloned = typeof response.clone === 'function' ? response.clone() : response;
        const text = await cloned.text();
        return text || fallback;
      } catch {
        return fallback;
      }
    }
  }

  private async invokeCheckout(
    planId: string,
    accessToken: string,
    couponCode?: string | null
  ): Promise<{ data: any; error: any }> {
    const url = `${this.supabaseService.supabaseUrl}/functions/v1/${this.checkoutFunction}`;
    const anonKey = this.supabaseService.supabaseKey;
    const normalizedToken = accessToken.replace(/^Bearer\s+/i, '').trim();
    const normalizedCouponCode = String(couponCode ?? '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9_-]/g, '')
      .slice(0, 40);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
        'x-user-authorization': `Bearer ${normalizedToken}`,
      },
      body: JSON.stringify({
        planId,
        couponCode: normalizedCouponCode || undefined,
      }),
      cache: 'no-store',
    });

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      return {
        data: null,
        error: {
          message: payload?.error ?? payload?.message ?? `HTTP ${response.status}`,
          context: {
            status: response.status,
            payload,
          },
        },
      };
    }

    return { data: payload, error: null };
  }

  async startCheckout(planId: string, options: StartCheckoutOptions = {}): Promise<void> {
    if (!this.authService.isAuthenticated()) {
      void this.logService.logWarn('Payment checkout requested without authenticated session', {
        domain: 'payment',
        event: 'payment.checkout.blocked_not_authenticated',
        status: 'blocked',
        planId,
      });
      this.authService.showNotification('error', 'Erro', 'Você precisa estar logado para comprar.');
      return;
    }

    const checkoutStartedAt = Date.now();
    void this.logService.logInfo('Payment checkout flow started', {
      domain: 'payment',
      event: 'payment.checkout.flow_started',
      status: 'started',
      planId,
      preopen: Boolean(options.preopen),
      couponCode: options.couponCode ? String(options.couponCode).toUpperCase() : null,
    });

    const checkoutTab = options.preopen ? this.openCheckoutLoadingTab() : null;

    try {
      const accessToken = await this.resolveAccessToken();

      if (!accessToken) {
        if (checkoutTab && !checkoutTab.closed) {
          checkoutTab.close();
        }
        this.authService.showNotification('error', 'Sessão expirada', 'Faça login novamente para continuar com a compra.');
        return;
      }

      let { data, error } = await this.invokeCheckout(planId, accessToken, options.couponCode);
      const status = Number((error as any)?.context?.status ?? 0);

      if (error && status === 401) {
        const retryRefresh = await this.supabase.auth.refreshSession();
        const retryToken = retryRefresh.data.session?.access_token?.trim();

        if (retryRefresh.error || !retryToken) {
          if (checkoutTab && !checkoutTab.closed) {
            checkoutTab.close();
          }
          this.authService.showNotification('error', 'Sessão inválida', 'Seu login expirou. Entre novamente para continuar.');
          await this.authService.signOut().catch(() => undefined);
          return;
        }

        const retried = await this.invokeCheckout(planId, retryToken, options.couponCode);
        data = retried.data;
        error = retried.error;
      }

      if (error) {
        throw error;
      }

      const checkoutUrl = data?.checkoutUrl;
      if (!checkoutUrl) {
        throw new Error('Não foi possível iniciar o checkout.');
      }

      const separator = checkoutUrl.includes('?') ? '&' : '?';
      const checkoutUrlWithNoCache = `${checkoutUrl}${separator}cb=${Date.now()}`;
      void this.logService.logInfo('Payment checkout URL generated', {
        domain: 'payment',
        event: 'payment.checkout.url_generated',
        status: 'success',
        planId,
        couponCode: options.couponCode ? String(options.couponCode).toUpperCase() : null,
        durationMs: Date.now() - checkoutStartedAt,
      });

      if (checkoutTab && !checkoutTab.closed) {
        checkoutTab.location.replace(checkoutUrlWithNoCache);
        return;
      }

      window.location.assign(checkoutUrlWithNoCache);
    } catch (err: any) {
      if (checkoutTab && !checkoutTab.closed) {
        checkoutTab.close();
      }
      const errorMessage = await this.getFunctionErrorMessage(err);
      const normalizedMessage = errorMessage.toLowerCase();
      const responseStatus = Number((err as any)?.context?.status ?? 0);
      const errorCode = String((err as any)?.context?.payload?.code ?? '').toUpperCase();
      const sessionIssue =
        responseStatus === 401
        || errorCode === 'AUTH_UNAUTHORIZED'
        || normalizedMessage.includes('invalid jwt')
        || normalizedMessage.includes('unauthorized')
        || normalizedMessage.includes('sessão expirada');

      const userMessage = sessionIssue
        ? 'Sua sessão expirou. Faça login novamente para continuar o checkout.'
        : errorMessage;
      void this.logService.logError('Payment checkout flow failed', err, {
        domain: 'payment',
        event: 'payment.checkout.flow_failed',
        status: 'error',
        planId,
        couponCode: options.couponCode ? String(options.couponCode).toUpperCase() : null,
        responseStatus,
        errorCode,
        sessionIssue,
        durationMs: Date.now() - checkoutStartedAt,
      });

      this.authService.showNotification('error', 'Falha no checkout', userMessage);
      if (sessionIssue) {
        await this.authService.signOut().catch(() => undefined);
        this.authService.showNotification('info', 'Sessão reiniciada', 'Faça login novamente para atualizar seu token.');
      }
    }
  }
}
